import axios from 'axios';

import { API_BASE_URL } from '../config/api';
import { getCognitoDomain, getUserPoolClientId } from '../config/runtimeConfig';
import {
  OAUTH_CODE_VERIFIER_STORAGE_KEY,
  OAUTH_STATE_STORAGE_KEY,
  generateCodeChallenge,
  generateCodeVerifier,
  generateState,
} from '../utils/pkce';
import { signalSessionExpired } from '../utils/sessionExpiry';

export interface CognitoRegisterRequest {
  email: string;
  password: string;
  full_name: string;
  phone_number?: string;
  tos_accepted: boolean;
  tos_version: string;
  email_marketing_opt_in?: boolean;
  email_marketing_consent_version?: string;
}

export interface CognitoVerifyEmailRequest {
  email: string;
  code: string;
}

export interface CognitoLoginRequest {
  email: string;
  password: string;
}

export interface CognitoForgotPasswordRequest {
  email: string;
}

export interface CognitoConfirmPasswordRequest {
  email: string;
  code: string;
  new_password: string;
}

export interface CognitoAuthTokens {
  access_token: string;
  id_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}

export interface CognitoUser {
  id: number;
  email: string;
  full_name: string;
  is_active: boolean;
}

export interface CognitoLoginResponse extends CognitoAuthTokens {
  user: CognitoUser;
}

export interface CognitoOAuthCallbackRequest {
  code: string;
  code_verifier: string;
}

export interface CognitoRegisterResponse {
  message: string;
  user_sub: string;
  email: string;
  requires_verification: boolean;
}

// Sliding-session policy. Access tokens live 1h and are refreshed
// silently; the *session* is bounded client-side by an inactivity idle timeout
// and an absolute cap measured from login. Cognito refresh-token rotation
// (enabled on the app client) is the server-side half — each refresh reissues
// the refresh token, so a session stays alive only while it keeps refreshing.
const PROACTIVE_REFRESH_LEAD_MS = 5 * 60 * 1000; // refresh this long before access-token expiry
const IDLE_TIMEOUT_MS = 8 * 60 * 60 * 1000; // no activity for 8h → force re-login
const ABSOLUTE_SESSION_CAP_MS = 7 * 24 * 60 * 60 * 1000; // 7d since login → force re-login
const ACTIVITY_WRITE_THROTTLE_MS = 30 * 1000; // coalesce activity writes to at most one / 30s

// Owned by this module (see the Cognito-storage-keys rule in frontend/CLAUDE.md).
const LOGIN_TIMESTAMP_KEY = 'cognito_login_timestamp'; // set at login only → absolute cap
const LAST_ACTIVITY_KEY = 'cognito_last_activity'; // updated on user activity → idle timeout

const REFRESH_TOKEN_PATH = '/auth/cognito/refresh-token';
export const TOO_MANY_ATTEMPTS_MESSAGE = 'Too many attempts. Please wait a minute and try again.';

const PASSWORD_FIELD = 'password';
const TOTP_CHALLENGE_NAME = 'SOFTWARE_TOKEN_MFA';
const TOTP_CODE_INVALID = 'mfa_code_invalid';
const TOTP_SESSION_EXPIRED = 'mfa_session_expired';
export const SIGN_IN_FAILED_MESSAGE = 'Sign-in failed. Please try again.';

/** The backend refused the new password itself (breached or space-edged), so the guest must edit it. */
export class PasswordRejectedError extends Error {
  readonly field = PASSWORD_FIELD;
}

/** The challenge a password log-in answers with when the account needs an authenticator code. */
export interface TotpChallenge {
  username: string;
  session: string;
}

export interface CompleteTotpChallengeRequest extends TotpChallenge {
  code: string;
}

/** The log-in was answered with an authenticator-code challenge; the host shows the code step. */
export class TotpChallengeRequiredError extends Error {
  constructor(readonly challenge: TotpChallenge) {
    super('Authenticator code required');
  }
}

/** The authenticator code was wrong or stale; the challenge is still live, so the guest can retry. */
export class TotpCodeRejectedError extends Error {}

/** The challenge session ran out; the guest has to start the log-in again. */
export class TotpSessionExpiredError extends Error {}

interface ErrorBody {
  detail?: string;
  field?: string;
  code?: string;
}

const isErrorBody = (data: unknown): data is ErrorBody =>
  typeof data === 'object' &&
  data !== null &&
  (!('detail' in data) || typeof data.detail === 'string') &&
  (!('field' in data) || typeof data.field === 'string') &&
  (!('code' in data) || typeof data.code === 'string');

const hasTotpChallengeName = (data: unknown): boolean =>
  typeof data === 'object' &&
  data !== null &&
  'challenge' in data &&
  data.challenge === TOTP_CHALLENGE_NAME;

const isTotpChallengeBody = (data: unknown): data is TotpChallenge =>
  typeof data === 'object' &&
  data !== null &&
  'username' in data &&
  typeof data.username === 'string' &&
  'session' in data &&
  typeof data.session === 'string';

const isLoginResponse = (data: unknown): data is CognitoLoginResponse =>
  typeof data === 'object' &&
  data !== null &&
  'access_token' in data &&
  typeof data.access_token === 'string' &&
  'id_token' in data &&
  typeof data.id_token === 'string' &&
  'refresh_token' in data &&
  typeof data.refresh_token === 'string' &&
  'expires_in' in data &&
  typeof data.expires_in === 'number' &&
  'token_type' in data &&
  typeof data.token_type === 'string' &&
  'user' in data &&
  typeof data.user === 'object' &&
  data.user !== null;

const errorBodyOf = (error: unknown): ErrorBody | undefined => {
  if (!axios.isAxiosError(error)) return undefined;
  const data: unknown = error.response?.data;
  return isErrorBody(data) ? data : undefined;
};

/**
 * Turns a failed call into the typed password refusal when the body names that field.
 * A 429 carries slowapi's `{"error": ...}` body with no `detail`, so it maps to the throttle copy.
 */
const toRegistrationOrResetError = (error: unknown, fallbackMessage: string): Error => {
  if (axios.isAxiosError(error) && error.response?.status === 429) {
    return new Error(TOO_MANY_ATTEMPTS_MESSAGE);
  }
  const body = errorBodyOf(error);
  const message = body?.detail || fallbackMessage;
  return body?.field === PASSWORD_FIELD ? new PasswordRejectedError(message) : new Error(message);
};

/** Maps a failed challenge call to its typed error; a 429 has no `detail` and gets the throttle copy. */
const toTotpChallengeError = (error: unknown): Error => {
  if (axios.isAxiosError(error) && error.response?.status === 429) {
    return new Error(TOO_MANY_ATTEMPTS_MESSAGE);
  }
  const body = errorBodyOf(error);
  const message = body?.detail || SIGN_IN_FAILED_MESSAGE;
  if (body?.code === TOTP_CODE_INVALID) return new TotpCodeRejectedError(message);
  if (body?.code === TOTP_SESSION_EXPIRED) return new TotpSessionExpiredError(message);
  return new Error(message);
};

class CognitoAuthService {
  private apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  private refreshPromise: Promise<CognitoAuthTokens> | null = null;
  private refreshTimer: NodeJS.Timeout | null = null;
  private pendingRequests: Array<{ resolve: Function; reject: Function }> = [];

  constructor() {
    // Add auth token to requests if available
    this.apiClient.interceptors.request.use((config) => {
      const token = this.getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      // Any request the user drives counts as activity for the idle timeout —
      // but NOT the automated refresh call itself, or the session would never
      // idle out (it refreshes ~hourly forever).
      if (!config.url?.includes(REFRESH_TOKEN_PATH)) {
        this.recordActivity();
      }
      return config;
    });

    // Add response interceptor to handle auth errors
    this.apiClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Check if it's an auth error and we haven't already tried to refresh
        if (
          (error.response?.status === 401 || error.response?.status === 403) &&
          !originalRequest._retry &&
          !originalRequest.url?.includes(REFRESH_TOKEN_PATH) &&
          this.getRefreshToken()
        ) {
          originalRequest._retry = true;

          try {
            await this.getValidAccessToken();
            // Update the authorization header with the new token
            const newToken = this.getAccessToken();
            if (newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return this.apiClient(originalRequest);
            }
          } catch (refreshError) {
            this.forceLogout();
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      },
    );

    // Start proactive refresh timer
    this.startProactiveRefresh();

    // Count genuine browsing (not just API calls) as activity, so a user
    // reading a page for a while without hitting the API isn't logged out.
    // Writes are throttled inside recordActivity, so these fire cheaply.
    if (typeof window !== 'undefined') {
      (['mousedown', 'keydown', 'touchstart', 'scroll'] as const).forEach((event) => {
        window.addEventListener(event, this.handleUserActivity, { passive: true });
      });
    }
  }

  private handleUserActivity = (): void => {
    this.recordActivity();
  };

  // Token management
  private setTokens(tokens: CognitoAuthTokens): void {
    localStorage.setItem('cognito_access_token', tokens.access_token);
    localStorage.setItem('cognito_id_token', tokens.id_token);
    localStorage.setItem('cognito_refresh_token', tokens.refresh_token);
    localStorage.setItem('cognito_expires_in', tokens.expires_in.toString());
    localStorage.setItem('cognito_token_timestamp', Date.now().toString());
  }

  getAccessToken(): string | null {
    return localStorage.getItem('cognito_access_token');
  }

  getIdToken(): string | null {
    return localStorage.getItem('cognito_id_token');
  }

  getRefreshToken(): string | null {
    return localStorage.getItem('cognito_refresh_token');
  }

  private isTokenExpired(): boolean {
    return this.getTimeUntilExpiry() <= 0;
  }

  private shouldRefreshProactively(): boolean {
    // Refresh if token expires within 5 minutes (300 seconds)
    return this.getTimeUntilExpiry() <= 5 * 60 * 1000;
  }

  private getTimeUntilExpiry(): number {
    const expiresIn = localStorage.getItem('cognito_expires_in');
    const timestamp = localStorage.getItem('cognito_token_timestamp');

    if (!expiresIn || !timestamp) return 0;

    const expirationTime = parseInt(timestamp) + parseInt(expiresIn) * 1000;
    return expirationTime - Date.now();
  }

  clearTokens(): void {
    this.stopProactiveRefresh();
    localStorage.removeItem('cognito_access_token');
    localStorage.removeItem('cognito_id_token');
    localStorage.removeItem('cognito_refresh_token');
    localStorage.removeItem('cognito_expires_in');
    localStorage.removeItem('cognito_token_timestamp');
    localStorage.removeItem(LOGIN_TIMESTAMP_KEY);
    localStorage.removeItem(LAST_ACTIVITY_KEY);
  }

  /**
   * Marks the start of a fresh session (password login or OAuth callback).
   * Sets the login timestamp (drives the absolute cap) and seeds activity.
   * Deliberately NOT called on refresh — the absolute cap is measured from
   * login, not from the last refresh.
   */
  private markSessionStart(): void {
    const now = Date.now().toString();
    localStorage.setItem(LOGIN_TIMESTAMP_KEY, now);
    localStorage.setItem(LAST_ACTIVITY_KEY, now);
  }

  /**
   * Records user activity for the idle timeout. Throttled so high-frequency
   * events (scroll, keydown) don't hammer localStorage.
   */
  recordActivity(): void {
    const now = Date.now();
    const last = parseInt(localStorage.getItem(LAST_ACTIVITY_KEY) || '0', 10);
    if (now - last < ACTIVITY_WRITE_THROTTLE_MS) return;
    localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
  }

  private isSessionIdle(): boolean {
    const last = localStorage.getItem(LAST_ACTIVITY_KEY);
    if (!last) return false; // unknown (e.g. pre-upgrade session) → don't force logout
    return Date.now() - parseInt(last, 10) >= IDLE_TIMEOUT_MS;
  }

  private isSessionPastAbsoluteCap(): boolean {
    const login = localStorage.getItem(LOGIN_TIMESTAMP_KEY);
    if (!login) return false; // unknown login time → cap not enforceable this session
    return Date.now() - parseInt(login, 10) >= ABSOLUTE_SESSION_CAP_MS;
  }

  private shouldEndSession(): boolean {
    return this.isSessionIdle() || this.isSessionPastAbsoluteCap();
  }

  private forceLogout(): void {
    this.clearTokens();
    signalSessionExpired();
  }

  // Auth methods
  async register(data: CognitoRegisterRequest): Promise<CognitoRegisterResponse> {
    try {
      const response = await this.apiClient.post('/api/v1/auth/cognito/register', data);
      return response.data;
    } catch (error: unknown) {
      throw toRegistrationOrResetError(error, 'Registration failed');
    }
  }

  async verifyEmail(
    data: CognitoVerifyEmailRequest,
  ): Promise<{ message: string; verified: boolean }> {
    try {
      const response = await this.apiClient.post('/api/v1/auth/cognito/verify-email', data);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 429) {
        throw new Error(TOO_MANY_ATTEMPTS_MESSAGE);
      }
      throw new Error(error.response?.data?.detail || 'Email verification failed');
    }
  }

  async resendVerificationCode(email: string): Promise<{ message: string }> {
    try {
      const response = await this.apiClient.post('/api/v1/auth/cognito/resend-code', { email });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 429) {
        throw new Error(TOO_MANY_ATTEMPTS_MESSAGE);
      }
      throw new Error(error.response?.data?.detail || 'Failed to resend verification code');
    }
  }

  private get loginEndpoint(): string {
    if (process.env.REACT_APP_DEV_AUTH_BYPASS === 'true') {
      return '/api/v1/auth/dev/login';
    }
    return '/api/v1/auth/cognito/login';
  }

  async login(data: CognitoLoginRequest): Promise<CognitoLoginResponse> {
    try {
      const response = await this.apiClient.post(this.loginEndpoint, data);

      const responseBody: unknown = response.data;
      if (hasTotpChallengeName(responseBody)) {
        if (!isTotpChallengeBody(responseBody)) {
          throw new Error(SIGN_IN_FAILED_MESSAGE);
        }
        throw new TotpChallengeRequiredError({
          username: responseBody.username,
          session: responseBody.session,
        });
      }

      // Check if there's another challenge (e.g., unverified email)
      if (response.data.challenge) {
        throw new Error(response.data.message || 'Additional verification required');
      }

      this.startSession(response.data);

      return response.data;
    } catch (error: unknown) {
      if (error instanceof TotpChallengeRequiredError) {
        throw error;
      }
      const errorMessage =
        errorBodyOf(error)?.detail ||
        (error instanceof Error ? error.message : undefined) ||
        'Login failed';

      // Handle specific error cases
      if (errorMessage.includes('Email not verified')) {
        throw new Error('EMAIL_NOT_VERIFIED');
      }

      throw new Error(errorMessage);
    }
  }

  /** Completes an authenticator-code challenge; success starts a session exactly as `login` does. */
  async completeTotpChallenge(
    request: CompleteTotpChallengeRequest,
  ): Promise<CognitoLoginResponse> {
    try {
      const response = await this.apiClient.post<unknown>('/api/v1/auth/cognito/mfa/challenge', {
        username: request.username,
        session: request.session,
        code: request.code,
      });
      if (!isLoginResponse(response.data)) {
        throw new Error(SIGN_IN_FAILED_MESSAGE);
      }
      this.startSession(response.data);
      return response.data;
    } catch (error: unknown) {
      throw toTotpChallengeError(error);
    }
  }

  private startSession(tokens: CognitoAuthTokens): void {
    this.setTokens(tokens);
    this.markSessionStart();
    this.startProactiveRefresh();
  }

  // Google OAuth (hosted-UI code flow, backend-mediated exchange) ──────────

  /**
   * Begin Google sign-in: generate PKCE + state, persist them in sessionStorage
   * for the callback route, and full-page-redirect to the Cognito hosted-UI
   * authorize endpoint scoped to the Google identity provider.
   */
  async beginGoogleSignIn(): Promise<void> {
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    const state = generateState();

    sessionStorage.setItem(OAUTH_CODE_VERIFIER_STORAGE_KEY, codeVerifier);
    sessionStorage.setItem(OAUTH_STATE_STORAGE_KEY, state);

    const domain = getCognitoDomain();
    const clientId = getUserPoolClientId();
    const redirectUri = `${window.location.origin}/auth/callback`;

    const params = new URLSearchParams({
      client_id: clientId,
      response_type: 'code',
      scope: 'openid email profile',
      redirect_uri: redirectUri,
      identity_provider: 'Google',
      state,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });

    window.location.assign(`https://${domain}/oauth2/authorize?${params.toString()}`);
  }

  /**
   * Exchange the hosted-UI authorization code for tokens via the backend
   * callback endpoint. The exchange is unauthenticated (no bearer yet); the
   * backend redeems the code server-side with the confidential client secret.
   */
  async exchangeOAuthCode(request: CognitoOAuthCallbackRequest): Promise<CognitoLoginResponse> {
    try {
      const response = await this.apiClient.post('/api/v1/auth/cognito/oauth-callback', request);
      return response.data;
    } catch (error: unknown) {
      const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data
        ?.detail;
      throw new Error(detail || 'Google sign-in failed. Please try again.');
    }
  }

  /**
   * Public entry point for persisting tokens obtained outside the password
   * login flow (the OAuth callback). Writes the same five cognito_* keys as the
   * password path and (re)starts the proactive-refresh timer.
   */
  storeTokens(tokens: CognitoAuthTokens): void {
    this.setTokens(tokens);
    this.markSessionStart();
    this.startProactiveRefresh();
  }

  async forgotPassword(email: string): Promise<{ message: string }> {
    try {
      const response = await this.apiClient.post('/api/v1/auth/cognito/forgot-password', { email });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 429) {
        throw new Error(TOO_MANY_ATTEMPTS_MESSAGE);
      }
      throw new Error(error.response?.data?.detail || 'Failed to initiate password reset');
    }
  }

  async confirmForgotPassword(
    data: CognitoConfirmPasswordRequest,
  ): Promise<{ message: string; success: boolean }> {
    try {
      const response = await this.apiClient.post('/api/v1/auth/cognito/confirm-password', data);
      return response.data;
    } catch (error: unknown) {
      throw toRegistrationOrResetError(error, 'Password reset failed');
    }
  }

  async refreshToken(): Promise<CognitoAuthTokens> {
    // Use mutex to prevent concurrent refresh attempts
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.performTokenRefresh();

    try {
      const tokens = await this.refreshPromise;

      // Resolve all pending requests
      this.pendingRequests.forEach((request) => request.resolve());
      this.pendingRequests = [];

      return tokens;
    } catch (error) {
      // Reject all pending requests
      this.pendingRequests.forEach((request) => request.reject());
      this.pendingRequests = [];

      throw error;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async performTokenRefresh(): Promise<CognitoAuthTokens> {
    try {
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const accessToken = this.getAccessToken();
      if (!accessToken) {
        throw new Error('No access token available');
      }

      // Enforce the sliding-session policy before spending a network call:
      // a session that has been idle too long, or has exceeded its absolute
      // age, must end rather than silently renew. This is the single choke
      // point for both the proactive timer and the reactive 401 path.
      if (this.shouldEndSession()) {
        throw new Error('Session ended: inactivity or maximum session age exceeded');
      }

      const response = await this.apiClient.post(`/api/v1${REFRESH_TOKEN_PATH}`, {
        refresh_token: refreshToken,
        access_token: accessToken,
      });

      // Update stored tokens. Persist the rotated refresh token when rotation
      // is enabled (Cognito returns a new one and invalidates the old after
      // the grace period); otherwise it is omitted and we keep the current one.
      const tokens = {
        ...response.data,
        refresh_token: response.data.refresh_token || refreshToken,
      };
      this.setTokens(tokens);

      // Restart proactive refresh timer with new token
      this.startProactiveRefresh();

      return tokens;
    } catch (error: any) {
      // Clear tokens if refresh fails
      this.clearTokens();
      this.stopProactiveRefresh();
      throw new Error(error.response?.data?.detail || 'Token refresh failed');
    }
  }

  isAuthenticated(): boolean {
    const token = this.getAccessToken();
    return !!token && !this.isTokenExpired();
  }

  logout(): void {
    this.clearTokens();
  }

  // Proactive refresh methods
  private startProactiveRefresh(): void {
    this.stopProactiveRefresh();

    if (!this.getRefreshToken()) return;

    // Fire shortly BEFORE the access token expires so the session is renewed
    // without interruption. Clamped to 0 so an already-expired token (e.g. a
    // tab reopened after a while) triggers an immediate refresh/idle-check.
    const delay = Math.max(0, this.getTimeUntilExpiry() - PROACTIVE_REFRESH_LEAD_MS);
    this.refreshTimer = setTimeout(() => {
      void this.proactiveRefreshTick();
    }, delay);
  }

  /**
   * Proactive-timer callback. Renews the session while it is still valid; if
   * the session has gone idle or hit its absolute cap, ends it instead. A
   * successful refresh re-arms the timer via performTokenRefresh.
   */
  private async proactiveRefreshTick(): Promise<void> {
    if (this.shouldEndSession()) {
      this.forceLogout();
      return;
    }
    try {
      await this.refreshToken();
    } catch {
      // Refresh failed (idle/cap guard, invalid refresh token, or network) —
      // performTokenRefresh has already cleared tokens; end the session in place.
      this.forceLogout();
    }
  }

  private stopProactiveRefresh(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  // Enhanced getValidAccessToken with proactive refresh and queuing
  async getValidAccessToken(): Promise<string | null> {
    const token = this.getAccessToken();

    if (!token) return null;

    // If refresh is in progress, queue this request
    if (this.refreshPromise) {
      return new Promise((resolve, reject) => {
        this.pendingRequests.push({
          resolve: () => resolve(this.getAccessToken()),
          reject: () => reject(new Error('Token refresh failed')),
        });
      });
    }

    // Check if we should refresh proactively
    if (this.shouldRefreshProactively()) {
      try {
        await this.refreshToken();
        return this.getAccessToken();
      } catch (error) {
        return null;
      }
    }

    // Check if token is expired
    if (this.isTokenExpired()) {
      try {
        await this.refreshToken();
        return this.getAccessToken();
      } catch (error) {
        return null;
      }
    }

    return token;
  }
}

export const cognitoAuthService = new CognitoAuthService();
export default cognitoAuthService;
